import React, { memo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { LeadsTable } from './LeadsTable';
import { LeadCalendar } from './LeadCalendar';
import { LeadColumns } from './LeadColumns';
import { LeadSummary } from './Header/LeadSummary';
import { useLeadsContext } from '../context/LeadsContext';

// Synchronized scroll container
const SynchronizedScrollContainer = styled.div`
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  width: 100%; /* Ensure full width */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }
`;

const FunnelContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: max-content;
`;

const FunnelContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SummaryContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 10px;
  position: relative;
  z-index: 10;
`;

const KanbanScrollArea = styled.div`
  position: relative;
  width: 100%;
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 5px;
  z-index: 1000;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  background: ${props => props.disabled ? '#d9d9d9' : '#1890ff'};
  border: none;
  color: white;
  font-size: 10px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.disabled ? '#d9d9d9' : '#40a9ff'};
    transform: ${props => props.disabled ? 'none' : 'scale(1.1)'};
  }
  
  &:active {
    transform: ${props => props.disabled ? 'none' : 'scale(0.95)'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LeftButton = styled(NavigationButton)`
  left: 5px;
`;

const RightButton = styled(NavigationButton)`
  right: 5px;
`;

export const DisplayModeRenderer = memo(() => {
  // Get all data and handlers from context
  const {
    displayMode,
    columns,
    showDrawer,
    setSelectedRows,
    setSelectedRowsKeys,
    period,
    openCalendarDrawer,
    setDate,
    moveLead,
    startShowingConfetti,
    date,
    isLoading,
    fetchQtddByMonth,
    loadMoreLeads,
    user,
    hideLeads,
    currentSummary,
    handleSummaryCardClick,
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    scrollLeft,
    scrollRight,
  } = useLeadsContext();
  switch (displayMode) {
    case "table":
      return (
        <LeadsTable />
      );

    case "calendar":
      return (
        <LeadCalendar
          columns={columns}
          openCalendarDrawer={openCalendarDrawer}
          setDate={setDate}
          date={date}
        />
      );

    case "funnel":
      return (
        <FunnelContainer style={{ marginTop: '20px' }}>
          <SummaryContainer>
            <LeadSummary 
              data={currentSummary} 
              onSummaryCardClick={handleSummaryCardClick}
              isLoading={isLoading}
            />
          </SummaryContainer>
          <KanbanScrollArea style={{ paddingTop: '30px' }}>
            <LeftButton onClick={scrollLeft} disabled={!canScrollLeft}>
              <FaChevronLeft />
            </LeftButton>
            <RightButton onClick={scrollRight} disabled={!canScrollRight}>
              <FaChevronRight />
            </RightButton>
            <SynchronizedScrollContainer ref={scrollContainerRef}>
              <FunnelContent>
                <LeadColumns />
              </FunnelContent>
            </SynchronizedScrollContainer>
          </KanbanScrollArea>
        </FunnelContainer>
      );

    default:
      return null;
  }
});

DisplayModeRenderer.displayName = 'DisplayModeRenderer';

DisplayModeRenderer.propTypes = {
  displayMode: PropTypes.oneOf(['table', 'calendar', 'funnel']).isRequired,
  columns: PropTypes.array,
  showDrawer: PropTypes.func.isRequired,
  setSelectedRows: PropTypes.func,
  setSelectedRowsKeys: PropTypes.func,
  period: PropTypes.object,
  openCalendarDrawer: PropTypes.func,
  setDate: PropTypes.func,
  moveLead: PropTypes.func,
  startShowingConfetti: PropTypes.func,
  date: PropTypes.string,
  isLoading: PropTypes.bool,
  fetchQtddByMonth: PropTypes.func,
  loadMoreLeads: PropTypes.func,
  user: PropTypes.object,
  hideLeads: PropTypes.bool,
  currentSummary: PropTypes.object,
  onSummaryCardClick: PropTypes.func,
  scrollContainerRef: PropTypes.object,
  canScrollLeft: PropTypes.bool,
  canScrollRight: PropTypes.bool,
  scrollLeft: PropTypes.func,
  scrollRight: PropTypes.func,
}; 